import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Trans } from 'react-i18next';
import { SketchPicker } from 'react-color';

import {
    Box,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    InputAdornment,
    Typography,
} from '@mui/material';

import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import CropFreeIcon from '@mui/icons-material/CropFree';
import CloseIcon from '@mui/icons-material/Close';
import ListAltIcon from '@mui/icons-material/ListAlt';

import StyledIconButton from '../../../../styled/IconButton';
import StyledButton from '../../../../styled/Button';
import StyledCard from '../../../../styled/Card';
import StyledTextField from '../../../../styled/TextField';

import store from '../../../../../store';
import {
    setNewZone,
    setSelectedZone,
    setZoneDraw,
    setZonesVisibility,
    setZoomToElementId,
    updateZone,
} from '../../../../../store/actions/Store';

import {
    createFloorPlanZone,
    deleteFloorPlanZone,
    updateFloorPlanZone,
} from '../../../../../services/zones';

import {
    handleZonesResponse,
} from '../../containers/StoreMapContainer';

import CustomDialog from '../../../../partials/CustomDialog';
import VirtualTable from '../../../../partials/VirtualTable';



// TODO: remove
export const exampleZones = [
    { id: 1, floor_plan_id: 2, name: 'Everyday Goods', color: 'lightgreen', position_x: 10, position_y: 10, width: 200, depth: 300 },
    { id: 2, floor_plan_id: 2, name: 'Special Sales', color: 'orange', position_x: 500, position_y: 10, width: 400, depth: 500 },
];

export const ColorDot = ({ color, size = 32 }) => {
    return <span style={{
        width: size,
        height: size,
        display: 'inline-block',
        backgroundColor: color || 'rgba(127,127,127,0.15)',
        borderRadius: '50%',
    }} />;
};

const ZoneInfo = ({ name, color, width, depth }) => {
    const w = Number(width);
    const d = Number(depth);
    const size = w * d / 100;
    const big = size > 100;
    const finalSize = big ? Math.round(size) / 100 : size;
    return (
        <Box display="flex" alignItems="center" gap={1}>
            <ColorDot color={color} />
            <Box display="flex" flexDirection="column">
                <Typography variant="button">{name || '-'}</Typography>
                <Typography variant="caption">
                    <Trans>Size</Trans>
                    {`: ${w} × ${d} = ${finalSize} ${big ? 'm' :  'cm'}²`}
                </Typography>
            </Box>
        </Box>
    );
};

const ZoneActions = ({ zone, visible, onEditClick, onVisibilityClick, onFocusClick, onReportClick }) => {
    const { id } = zone;
    return (
        <Box>
            <StyledIconButton size="small" color="secondary" onClick={() => onReportClick(zone)}>
                <ListAltIcon />
            </StyledIconButton>
            <StyledIconButton size="small" color="secondary" onClick={() => onVisibilityClick(id)}>
                {visible ? <VisibilityIcon /> : <VisibilityOffIcon />}
            </StyledIconButton>
            <StyledIconButton size="small" color="secondary" onClick={() => onFocusClick(zone)}>
                <CropFreeIcon />
            </StyledIconButton>
            <StyledIconButton size="small" color="secondary" onClick={() => onEditClick(zone)}>
                <EditIcon />
            </StyledIconButton>
        </Box>
    );
};

const Zone = ({ zone, visible, onEditClick, onVisibilityClick, onFocusClick, onReportClick, onClick }) => {
    return (
        <StyledCard sx={{ p: 1, borderRadius: 1 }} onClick={onClick}>
            <Box display="flex" justifyContent="space-between" alignItems="center" gap={1}>
                <ZoneInfo {...zone} />
                <ZoneActions
                    zone={zone}
                    visible={visible}
                    onEditClick={onEditClick}
                    onFocusClick={onFocusClick}
                    onReportClick={onReportClick}
                    onVisibilityClick={onVisibilityClick}
                />
            </Box>
        </StyledCard>
    );
};

const ZoneForm = ({ open, zone, onConfirm, onDelete, onClose }) => {
    const [picker, setPicker] = useState(false);
    const [name, setName] = useState('');
    const [color, setColor] = useState('');

    const edit = zone !== null && zone.id !== undefined;

    const currentName = edit ? zone.name : '';
    const currentColor = edit ? zone.color : 'black';

    const handleClose = () => {
        setPicker(false);
        setName(currentName);
        setColor(currentColor);
        onClose();
    };

    const toggleColorPick = () => {
        setPicker(v => !v);
    };

    const handleColorChange = (c) => {
        setColor(c.hex);
    };

    const handleConfrm = () => {
        onConfirm({ ...zone, name, color });
        handleClose();
    };

    const handleDelete = () => {
        // TODO: confirm delete dialog
        if (!window.confirm('Are you sure you want to delete this zone?')) return;
        onDelete(zone);
        handleClose();
    };

    useEffect(() => {
        setName(currentName);
        setColor(currentColor);
    }, [currentName, currentColor]);

    return (
        <Dialog open={open} onClose={handleClose}>
            <DialogTitle display="flex" justifyContent="space-between" gap={2}>
                {edit ? (<>
                    <Trans>Edit zone</Trans>
                    {`: ${currentName}`}
                </>) : (
                    <Trans>Create zone</Trans>
                )}
                <StyledIconButton
                    color="secondary"
                    onClick={handleClose}
                    sx={{ mx: -2, my: -1 }}
                >
                    <CloseIcon />
                </StyledIconButton>
            </DialogTitle>
            <DialogContent sx={{ px: 2, py: 0 }}>
                <Box py={1}>
                    <StyledTextField
                        fullWidth
                        size="small"
                        variant="outlined"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        label={<Trans>Name</Trans>}
                    />
                </Box>
                <Box py={1}>
                    <StyledTextField
                        fullWidth
                        size="small"
                        variant="outlined"
                        value={color}
                        onChange={(e) => setColor(e.target.value)}
                        label={<Trans>Color</Trans>}
                        InputProps={{
                            sx: { paddingRight: 0 },
                            endAdornment: (
                                <InputAdornment position="end">
                                    <StyledIconButton onClick={toggleColorPick}>
                                        <ColorDot color={color} size={20} />
                                    </StyledIconButton>
                                </InputAdornment>
                            )
                        }}
                    />
                </Box>
                <Box py={1}>
                    {picker && (
                        <SketchPicker disableAlpha color={color} onChange={handleColorChange} />
                    )}
                </Box>
            </DialogContent>
            <DialogActions>
                {edit && (
                    <StyledButton
                        size="small"
                        color="secondary"
                        variant="contained"
                        onClick={handleDelete}
                    >
                        <Trans>Delete</Trans>
                    </StyledButton>
                )}
                <StyledButton
                    size="small"
                    color="secondary"
                    variant="contained"
                    onClick={handleClose}
                >
                    <Trans>Cancel</Trans>
                </StyledButton>
                <StyledButton
                    size="small"
                    color="primary"
                    variant="contained"
                    disabled={!name || !color}
                    onClick={handleConfrm}
                >
                    {edit ? <Trans>Save Changes</Trans> : <Trans>Create</Trans>}
                </StyledButton>
            </DialogActions>
        </Dialog>
    );
};

const columns = [
    { id: 'name', title: 'Name', numeric: false,
        style: { overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis', minWidth: '320px' } },
    { id: 'number', title: 'Number', numeric: false,
        style: { overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis', minWidth: '120px' } },
    { id: 'width', title: 'Width', numeric: true, double: true },
    { id: 'height', title: 'Height', numeric: true, double: true },
    { id: 'depth', title: 'Depth', numeric: true, double: true },
];

const ZoneReport = ({ zone, onClose }) => {
    const storeData = useSelector((state) => state.store.storeData);

    const zpx = zone ? Number(zone.position_x) : 0;
    const zpy = zone ? Number(zone.position_y) : 0;
    const zw = zone ? Number(zone.width) : 0;
    const zd = zone ? Number(zone.depth) : 0;

    const elements = storeData ? storeData?.floor_plan_elements || [] : [];

    const zoneElements = elements.filter(element => {
        const px = Number(element.positionX);
        const py = Number(element.positionY);
        return px >= zpx && px <= zpx + zw && py >= zpy && py <= zpy + zd;
    }).filter(element => !element.is_wall);

    const handleClose = () => {
        if (onClose) onClose();
    };

    return (
        <CustomDialog
            fullWidth
            maxWidth="lg"
            scroll="paper"
            open={!!zone}
            onClose={handleClose}
            title={<>
                {`${zone ? zone.name : ''} `}
                <Trans>Zone Elements Report</Trans>
            </>}
        >
            {zoneElements && (
                <VirtualTable filter rows={zoneElements} columns={columns} />
            )}
        </CustomDialog>
    );
};

const defaultZone = {
    id: undefined,
    name: '', color: 'black',
    position_x: 30, position_y: 30,
    width: 100, depth: 100,
};

const ZonesTab = () => {
    const [openForm, setOpenForm] = useState(false);
    const [editZone, setEditZone] = useState(null);
    const [reportZone, setReportZone] = useState(null);

    const zones = useSelector((state) => state.store.zones) || [];
    const newZone = useSelector((state) => state.store.newZone);
    const zonesVisibility = useSelector((state) => state.store.zonesVisibility) || {};
    const isZoneDrawEnabled = useSelector((state) => state.store.isZoneDrawEnabled);
    const storeData = useSelector((state) => state.store.storeData);

    const st = storeData ? storeData.floor_plan : null;
    const stid = st ? st.id : null;

    const handleCreateZone = () => {
        handleFormOpen(defaultZone);
    }

    const handleDrawZone = () => {
        store.dispatch(setZoneDraw(true));
        // TODO: cancel draw mode on press Esc
    }

    const handleFormOpen = (zone = null) => {
        setEditZone(zone);
        setOpenForm(true);
    }

    const handleFormClose = () => {
        setOpenForm(false);
        setEditZone(null);
    };

    const handleConfirm = (zone) => {
        if (zone.id) {
            store.dispatch(updateZone(zone));
            updateFloorPlanZone(zone).then(handleZonesResponse);
        } else {
            const data = { ...zone, floor_plan_id: stid };
            createFloorPlanZone(data).then(handleZonesResponse);
        }
    };

    const handleDelete = (zone) => {
        deleteFloorPlanZone(zone).then(handleZonesResponse)
    };

    const toggleZoneVisibility = (id) => {
        const visible = typeof zonesVisibility[id] == 'boolean' ? zonesVisibility[id] : true;
        store.dispatch(setZonesVisibility({ ...zonesVisibility, [id]: !visible }));
    };

    const handleZoneClick = (event, zone) => {
        event.stopPropagation();
        store.dispatch(setSelectedZone(zone));
    };

    const handleZoneFocus = (zone) => {
        store.dispatch(setZoomToElementId(`Zone_${zone.id}`));
    };

    const handleShowZoneReport = (zone) => {
        setReportZone(zone);
    };

    useEffect(() => {
        if (newZone) {
            handleFormOpen({ ...defaultZone, ...newZone });
            store.dispatch(setNewZone(null));
        }
    }, [newZone]);

    return (
        <Box pt={1} height="100%">
            <Box display="flex" gap={2}>
                <StyledButton fullWidth size="small" color="primary" variant="contained" onClick={handleCreateZone} disabled={isZoneDrawEnabled}>
                    <Trans>Create zone</Trans>
                </StyledButton>
                <StyledButton fullWidth size="small" color="primary" variant="contained" onClick={handleDrawZone} disabled={isZoneDrawEnabled}>
                    <Trans>Draw new zone</Trans>
                </StyledButton>
            </Box>
            <Box pt={2} display="flex" flexDirection="column" gap={1} overflow="auto">
                {zones.map(zone => (
                    <Zone
                        key={zone.id}
                        zone={zone}
                        visible={zonesVisibility[zone.id]}
                        onEditClick={handleFormOpen}
                        onFocusClick={handleZoneFocus}
                        onReportClick={handleShowZoneReport}
                        onVisibilityClick={toggleZoneVisibility}
                        onClick={(e) => handleZoneClick(e, zone)}
                    />
                ))}
                <Box display="flex" justifyContent="center">
                    <Typography variant="caption">
                        <Trans>Items total</Trans>
                        {`: ${zones.length}`}
                    </Typography>
                </Box>
            </Box>
            <ZoneReport
                zone={reportZone}
                onClose={() => setReportZone(null)}
            />
            <ZoneForm
                open={openForm}
                zone={editZone}
                onConfirm={handleConfirm}
                onDelete={handleDelete}
                onClose={handleFormClose}
            />
        </Box>
    );
};

export default ZonesTab;
